import { useCallback, useEffect, useState } from 'react';
import { Select, Table, Tag, message } from 'antd';
import { collection, getDocs, query, where } from 'firebase/firestore';
import PropTypes from 'prop-types';
import {
  notificationTimeout,
  notificationType,
  scrollHeight,
} from '../common/constants';
import notValid from '../common/notValid';
import pluralise from '../common/pluralise';
import { db } from '../firebase';

// D24 (2026-05-13): prop renamed siteName → siteId; carries the UUID
// from sites/{siteId}.
export default function SiteConfigResidents({ siteId }) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [blockFilter, setBlockFilter] = useState(null);
  const [blockOptions, setBlockOptions] = useState([]);

  const [messageApi, contextHolder] = message.useMessage();

  const { timeoutError } = notificationTimeout;
  const { typeError } = notificationType;

  const openMessage = useCallback(
    (content, duration, type) => {
      messageApi.open({ content, duration, type });
    },
    [messageApi],
  );

  const fetchResidents = useCallback(
    async (nameSite) => {
      if (notValid(nameSite)) return;
      setLoading(true);
      try {
        const residentRef = collection(db, 'accounts');
        // V2 schema: accounts use `assignedSite` (UUID), not `siteName`.
        // Filter to residents only — site staff (accessLevel < 21) live
        // in the Site Staff tab. Without this filter SuperAdmins +
        // Admins appear here with empty block/unit/type columns.
        const q = query(residentRef, where('assignedSite', '==', nameSite));
        const querySnapshot = await getDocs(q);
        const residents = [];
        const blockSet = new Set();

        querySnapshot.forEach((doc) => {
          const dict = doc.data();
          // Filter residents-only client-side. Done after fetch because
          // accessLevel may be absent on legacy docs, and an explicit
          // `>= 21` query would silently drop those rows.
          const level = dict.accessLevel;
          if (typeof level === 'number' && level < 21) return;

          residents.push({
            key: doc.id,
            blockNumber: dict.blockNumber || '--',
            unitNumber: dict.unitNumber || '--',
            fullname: dict.fullname || '--',
            type: dict.type || '--',
            contactNumber: dict.contactNumber || '--',
            status: dict.status,
            email: dict.email || '--',
          });
          if (dict.blockNumber) blockSet.add(dict.blockNumber);
        });

        residents.sort((a, b) => {
          const blockCmp = String(a.blockNumber).localeCompare(String(b.blockNumber));
          if (blockCmp !== 0) return blockCmp;
          return String(a.unitNumber).localeCompare(String(b.unitNumber));
        });

        setData(residents);
        setBlockOptions(
          Array.from(blockSet)
            .sort()
            .map((b) => ({ label: b, value: b })),
        );

        // D24 (2026-05-13): no success/info toast on automatic loads — they
        // fire twice under React.StrictMode dev and don't add value when
        // the empty/populated state is already visible in the table.
      } catch (error) {
        console.error('-- Error fetching residents:', error);
        openMessage('Error fetching residents', timeoutError, typeError);
      } finally {
        setLoading(false);
      }
    },
    [openMessage, timeoutError, typeError],
  );

  useEffect(() => {
    fetchResidents(siteId);
  }, [fetchResidents, siteId]);

  const filteredData = blockFilter
    ? data.filter((r) => r.blockNumber === blockFilter)
    : data;

  const columns = [
    {
      title: 'Block',
      dataIndex: 'blockNumber',
      width: 100,
      sorter: (a, b) => a.blockNumber.localeCompare(b.blockNumber),
    },
    {
      title: 'Unit',
      dataIndex: 'unitNumber',
      width: 100,
      sorter: (a, b) => a.unitNumber.localeCompare(b.unitNumber),
    },
    {
      title: 'Name',
      dataIndex: 'fullname',
      width: 180,
      sorter: (a, b) => a.fullname.localeCompare(b.fullname),
    },
    {
      title: 'Type',
      dataIndex: 'type',
      width: 120,
      render: (val) => <Tag>{val}</Tag>,
      filters: [
        { text: 'SuperAdmin', value: 'SuperAdmin' },
        { text: 'Admin', value: 'Admin' },
        { text: 'Owner', value: 'Owner' },
        { text: 'Tenant', value: 'Tenant' },
      ],
      onFilter: (value, record) => record.type === value,
    },
    {
      title: 'Contact',
      dataIndex: 'contactNumber',
      width: 130,
    },
    {
      title: 'Email',
      dataIndex: 'email',
      width: 200,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      width: 100,
      render: (val) =>
        val === 1 ? (
          <Tag color="success">Active</Tag>
        ) : (
          <Tag color="default">Inactive</Tag>
        ),
    },
  ];

  return (
    <>
      {contextHolder}
      <div style={{ marginBottom: 12 }}>
        <Select
          allowClear
          onChange={(val) => setBlockFilter(val || null)}
          options={blockOptions}
          placeholder="Filter by block"
          style={{ width: 200 }}
          value={blockFilter}
        />
      </div>
      <Table
        columns={columns}
        dataSource={filteredData}
        loading={loading}
        pagination={{
          position: ['bottomCenter'],
          showSizeChanger: true,
          showTotal: (total, range) =>
            `${range[0]} - ${range[1]} of ${total} ${pluralise(total, 'resident')}`,
        }}
        rowKey="key"
        scroll={{ x: 800, y: scrollHeight }}
      />
    </>
  );
}

SiteConfigResidents.propTypes = {
  siteId: PropTypes.string.isRequired,
};
